// The quotation grid's "Markup for all lines" controls, lifted out of QuotationDetailPage
// (CS-1) so the question they ask can be tested without mounting the page.
//
// It asked with `window.confirm`. A native dialog freezes the tab, so Report a bug could not
// be clicked while it was up (ClickUp 14yj85ny3xf), and it stalled the QUO-00070 check. It now
// asks through useConfirm like the per-line Markup cell does: Escape cancels, OK must be clicked.
// Returns a fragment: the page's toolbar row lays these out beside its Reset columns button.
import React, { useRef, useState } from 'react';
import { Undo2 } from 'lucide-react';
import Button from '../../components/ui/Button';
import { useConfirm } from '../../components/ui/ConfirmProvider';
import { useToast } from '../../context/ToastContext';
import { markupEditProblem } from './lineEditChecks';

export interface GlobalMarkupBarProps {
  lineCount: number;
  applying: boolean;
  /** The rate of the bulk apply that can still be undone, or null when there is none. */
  undoPct: number | null;
  /** Writes the rate to every line. Resolves true once it landed, which clears the box. */
  onApply: (pct: number) => Promise<boolean>;
  onUndo: () => void;
}

export const GlobalMarkupBar: React.FC<GlobalMarkupBarProps> = ({ lineCount, applying, undoPct, onApply, onUndo }) => {
  const confirm = useConfirm();
  const toast = useToast();
  const [input, setInput] = useState('');
  // One question at a time. A second Enter would ask again, and ConfirmProvider answers the
  // open question "no" to make room for the new one. A ref, not state: the second keydown
  // arrives before a state update could be read.
  const askingRef = useRef(false);

  const apply = async () => {
    if (askingRef.current || applying) return;
    const pct = parseFloat(input);
    if (!Number.isFinite(pct)) {
      toast.error('Enter a valid markup percentage.');
      return;
    }
    const problem = markupEditProblem(pct);
    if (problem) {
      toast.error(problem);
      return;
    }
    askingRef.current = true;
    try {
      const ok = await confirm({
        title: `Apply ${pct}% markup to all ${lineCount} line item(s)?`,
        summary: "Every line's selling price is recalculated from its cost. You can still adjust individual lines afterwards.",
        confirmLabel: 'Apply markup',
      });
      if (ok && (await onApply(pct))) setInput('');
    } finally {
      askingRef.current = false;
    }
  };

  return (
    <>
      <span className="text-xs font-medium text-slate-700">
        Markup for all lines:
      </span>
      <div className="flex items-center">
        <input
          type="number"
          step="0.5"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void apply();
          }}
          placeholder="e.g. 40"
          disabled={applying}
          className="w-20 h-8 px-2 text-xs text-slate-900 border border-slate-200 rounded-l-md focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 disabled:opacity-50"
          title="Set this markup % on every line item in one action"
        />
        <span className="inline-flex items-center h-8 px-1.5 text-xs border border-l-0 border-slate-200 rounded-r-md bg-white text-slate-500">%</span>
      </div>
      {/* Secondary: the header's Send quote / Confirm order is this screen's
          one filled button. */}
      <Button
        size="sm"
        onClick={() => void apply()}
        disabled={input.trim() === ''}
        loading={applying}
      >
        {applying ? 'Applying…' : 'Apply to all lines'}
      </Button>
      {undoPct !== null && (
        <Button
          size="sm"
          variant="ghost"
          onClick={onUndo}
          disabled={applying}
          title={`Restore the per-line markups from before the ${undoPct}% bulk apply`}
          icon={<Undo2 aria-hidden="true" className="h-4 w-4" />}
        >
          Undo {undoPct}%
        </Button>
      )}
      <span className="text-xs text-slate-500">
        Overrides every line's markup. Individual lines can still be edited afterwards.
      </span>
    </>
  );
};

export default GlobalMarkupBar;
